import { FieldNames, FormType } from "@/enums";
import { IDictionary, ISelectOption } from "@/interfaces";

import { UserOptionLabel, ViperSelect } from "@/components";
import { RolePurposeConstants } from "@/enums/RolePurposeConstants";
import { IDefaultProjectUser } from "@/interfaces/Project/Roles/IDefaultProjectUser";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import {
  useGetFormLookupsQuery,
  useGetUserQuery,
  useSuggestUsersQuery,
} from "@/services";
import { ProjectRolePurpose } from "@/types/ProjectRolePurpose";
import { Form, Modal, Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { v4 } from "uuid";

const { Option } = Select;

interface IProps {
  regionRole: IRegionalProjectRole;
  open: boolean;
  onClose(): void;
  onOk(data: IDefaultProjectUser): void;
  defaultUserId?: string;
}

export const AddEditDefaultUserModal = ({
  regionRole,
  open,
  onClose,
  onOk,
  defaultUserId,
}: IProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [userQuery, setUserQuery] = useState<string>("");
  const [projectTypes, setProjectTypes] = useState<ISelectOption[]>([]);
  const [productCategories, setProductCategories] = useState<ISelectOption[]>(
    []
  );
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [workingDefaultUser, setWorkingDefaultUser] =
    useState<IDefaultProjectUser>();
  const purposes: IDictionary[] = [
    {
      key: RolePurposeConstants.ProjectTeam,
      value: intl.formatMessage({ id: "projectRoles.purposes.projectTeam" }),
    },
    {
      key: RolePurposeConstants.MilestoneApprover,
      value: intl.formatMessage({ id: "projectRoles.purposes.approver" }),
    },
    {
      key: RolePurposeConstants.Distribution,
      value: intl.formatMessage({ id: "projectRoles.purposes.distribution" }),
    },
  ];

  const { data: userSuggestions, isFetching: fetchingSuggestions } =
    useSuggestUsersQuery(
      { query: userQuery },
      {
        skip: !userQuery,
      }
    );

  const { data: forms } = useGetFormLookupsQuery();

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  const { data: user, isFetching } = useGetUserQuery(
    workingDefaultUser?.userId,
    {
      skip: !workingDefaultUser?.userId || workingDefaultUser?.userId === "",
    }
  );
  const finalUser =
    isFetching || !workingDefaultUser?.userId ? undefined : user;
  const [selectedUserId, setSelectedUserId] = useState<string>();

  const { data: selectedUser } = useGetUserQuery(selectedUserId, {
    skip: !selectedUserId || selectedUserId === "",
  });

  useEffect(() => {
    if (defaultUserId === undefined || defaultUserId === "") {
      setWorkingDefaultUser(undefined);
    } else {
      setWorkingDefaultUser(
        regionRole.defaultProjectUsers.find((x) => x.id === defaultUserId)
      );
    }
  }, [defaultUserId, regionRole.defaultProjectUsers]);

  useEffect(() => {
    const intake = forms?.intake;
    if (!intake) return;

    setProjectTypes(
      Object.keys(intake?.projectType).map((key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProjectType,
          key
        ),
      }))
    );

    setProductCategories(
      Object.keys(intake?.productCategory).map((key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProductCategory,
          key
        ),
      }))
    );
  }, [forms?.intake, getPropertyValueDisplay]);

  const handleAdd = (): void => {
    form.validateFields().then(
        (data: {
          userId: string;
          publicProjectTypeIds: string[];
          publicProductCategoryIds: string[];
          purposes: ProjectRolePurpose[];
        }) => {
         const id = workingDefaultUser?.id && workingDefaultUser.id !== ""
          ? workingDefaultUser.id
          : v4();

          onOk({
            id,
            publicId: id,
            ...data,
            userId: workingDefaultUser?.userId ?? selectedUserId ?? "",
          });
          form.resetFields();
        }
      );
  };

  const onCancel = (): void => {
    form.resetFields();
    onClose();
  };

  const validate = (): void => {
    setIsDuplicate(false);
    const userId = form.getFieldValue("userId");
    setSelectedUserId(userId?.value ?? workingDefaultUser?.userId);
    if (userId && typeof userId === "string") {
      //TS gets angry if we don't default these values.  None of these should actually be null here.
      const newUser = {
        id: workingDefaultUser?.id ?? "",
        publicId: workingDefaultUser?.publicId ?? "",
        publicProductCategoryIds: workingDefaultUser?.publicProductCategoryIds ?? [],
        publicProjectTypeIds: workingDefaultUser?.publicProjectTypeIds ?? [],
        userId: userId,
        purposes: workingDefaultUser?.purposes ?? [],
      };
      setWorkingDefaultUser(newUser);
    }

    const publicProjectTypeIds = form.getFieldValue("publicProjectTypeIds");
    const publicProductCategoryIds = form.getFieldValue("publicProductCategoryIds");
    const defaultProjectUsers = regionRole.defaultProjectUsers.find(
      (x) => x.userId === userId
    );
    if (defaultProjectUsers) {
      const dupeProjectTypes: string[] = [];
      const dupeProductCategories: string[] = [];
      defaultProjectUsers.publicProjectTypeIds?.forEach((projType) => {
        publicProjectTypeIds?.forEach((selectedProjectType: string) => {
          if (projType === selectedProjectType) {
            dupeProjectTypes.push(projType);
          }
        });
        defaultProjectUsers.publicProductCategoryIds?.forEach((prodCat) => {
          publicProductCategoryIds?.forEach((selectedProductCategory: string) => {
            if (selectedProductCategory === prodCat) {
              dupeProductCategories.push(prodCat);
            }
          });
        });
        if (dupeProjectTypes.length > 0 && dupeProductCategories.length > 0) {
          const projectType = getPropertyValueDisplay(
            FormType.Intake,
            FieldNames.ProjectType,
            dupeProjectTypes[0]
          );
          const productCategory = getPropertyValueDisplay(
            FormType.Intake,
            FieldNames.ProductCategory,
            dupeProductCategories[0]
          );
          setIsDuplicate(true);
          toast.error(
            intl.formatMessage(
              { id: "projectRoles.duplicateDefaultUser" },
              {
                user: selectedUser.displayName,
                projectType: projectType,
                productCategory: productCategory,
              }
            )
          );
        }
      });
    }
  };

  return (
    <Modal
      centered
      closable={false}
      title={intl.formatMessage({
        id: workingDefaultUser?.id
          ? "projectRoles.editDefaultUser"
          : "projectRoles.addDefaultUser",
      })}
      open={open}
      cancelText={intl.formatMessage({ id: "general.cancel" })}
      okText={intl.formatMessage({
        id: workingDefaultUser?.id ? "general.ok" : "general.add",
      })}
      onCancel={onCancel}
      onOk={handleAdd}
      okButtonProps={{ disabled: isDuplicate }}
      destroyOnHidden={true}
    >
      {!isFetching ? (
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="userId"
            label={intl.formatMessage({ id: "projectRoles.userName" })}
            initialValue={{
              label: finalUser?.displayName,
              value: finalUser?.userId,
            }}
            rules={[{ required: true, message: "" }]}
          >
            <ViperSelect
              optionLabelProp="label"
              customOptions={userSuggestions?.map(
                ({
                  document: { id, displayName, department, email, jobTitle },
                }) => (
                  <Option key={id} label={displayName}>
                    <UserOptionLabel
                      displayName={displayName}
                      department={department}
                      email={email}
                      jobTitle={jobTitle}
                    />
                  </Option>
                )
              )}
              loading={fetchingSuggestions}
              onFetchSuggestions={(searchTerm): void => {
                setUserQuery(searchTerm);
              }}
              onChange={validate}
              data-test-id="add-users-dialog-users-add"
            />
          </Form.Item>
          <Form.Item
            name="publicProjectTypeIds"
            label={intl.formatMessage({ id: "projectRoles.projectType" })}
            rules={[{ required: true, message: "" }]}
            initialValue={workingDefaultUser?.publicProjectTypeIds}
          >
            <Select
              allowClear
              mode="multiple"
              options={projectTypes}
              onChange={validate}
              data-test-id="project-type-select"
            />
          </Form.Item>
          <Form.Item
            name ="publicProductCategoryIds"
            label={intl.formatMessage({ id: "projectRoles.productCategory" })}
            rules={[{ required: true, message: "" }]}
            initialValue = { workingDefaultUser?.publicProductCategoryIds }
          >
            <Select
              allowClear
              mode="multiple"
              options={productCategories}
              onChange={validate}
              data-test-id="default-user-add-product-categories"
            />
          </Form.Item>
          <Form.Item
            name="purposes"
            label={intl.formatMessage({ id: "projectRoles.purpose" })}
            rules={[{ required: true, message: "" }]}
            initialValue={workingDefaultUser?.purposes}
          >
            <Select allowClear mode="multiple" options={purposes} />
          </Form.Item>
        </Form>
      ) : (
        <Spin />
      )}
    </Modal>
  );
};
